import { AgentProvider, SidePanel, createAgentApi } from "@repo/ui/chat";
import type { HistoryEntry } from "@repo/ui/chat";
import { useCallback, useMemo } from "react";
import { useActiveSession } from "./hooks/useActiveSession";
import { useAgentCapabilities } from "./hooks/useAgentCapabilities";
import { useAgentSessions } from "./hooks/useAgentSessions";
import { useGainsightIdentify } from "./hooks/useGainsightIdentify";
import { useHostTheme } from "./hooks/useHostTheme";
import { useOrgChange } from "./hooks/useOrgChange";
import { useSessionMessages } from "./hooks/useSessionMessages";
import { appStyles } from "./App.styles";
import { config } from "./config";
import type { AppProps } from "./App.types";

export const App = ({ creds, hostContext, host, onClose }: AppProps) => {
  useHostTheme(hostContext);
  useGainsightIdentify(creds, hostContext, host);

  const conversation = useMemo(() => hostContext.channel("conversation"), [hostContext]);
  const getMrn = useCallback(() => conversation.getEntry()?.metadata.mrn, [conversation]);
  const baseUrl = config.agentBaseUrl;
  const getToken = useCallback(() => creds.getValidToken(), [creds]);
  const api = useMemo(() => createAgentApi(baseUrl, getToken), [baseUrl, getToken]);

  // clientApp stays "ios" | "android" | "web" even when the Desktop App wraps a web session — see
  // clientAppDesktopVersion, whose presence (not clientApp) is what signals a DA session.
  const clientApp = host.platform === "standalone" ? undefined : host.platform;
  const clientAppVersion = host.version ?? undefined;
  const clientAppDesktopVersion = host.desktopVersion;

  const { activeSession, providerKey, select, startNewChat, discard } = useActiveSession();

  const {
    sessions,
    loading: sessionsLoading,
    error: sessionsError,
    refetch: refetchSessions,
    rename: renameSession,
    remove: removeSession,
  } = useAgentSessions(api);
  const welcomeCards = useAgentCapabilities(api);
  const { messages: seedMessages, loading: messagesLoading } = useSessionMessages(
    api,
    activeSession,
  );

  // Org switch re-scopes both the session and history: start fresh, then refetch the list.
  const handleOrgChange = useCallback(() => {
    startNewChat();
    refetchSessions();
  }, [refetchSessions, startNewChat]);

  useOrgChange(hostContext, handleOrgChange);

  const handleRenameHistoryEntry = useCallback(
    (entry: HistoryEntry, title: string) => renameSession(entry.id, title),
    [renameSession],
  );

  const handleDeleteHistoryEntry = useCallback(
    async (entry: HistoryEntry) => {
      await removeSession(entry.id);
      // If the deleted conversation was still open, fall back to a fresh chat rather than a dead thread.
      discard(entry.id);
    },
    [removeSession, discard],
  );

  // A host conversation switch deliberately does not reset the chat — the agent session carries
  // on and simply reports the new conversation on its next turn (see `getConversation`).

  // A provider (and the runtime it seeds) is only mounted once we know what to seed it with —
  // immediately for a fresh chat, or once the selected session's history has loaded.
  const readyToRenderChat = !activeSession || !messagesLoading;

  if (!readyToRenderChat) {
    return <div className={appStyles.loading}>Loading conversation…</div>;
  }

  return (
    <AgentProvider
      key={providerKey}
      api={api}
      activeSession={activeSession}
      initialMessages={seedMessages}
      getConversation={conversation.getEntry}
      getMrn={getMrn}
      clientApp={clientApp}
      clientAppVersion={clientAppVersion}
      clientAppDesktopVersion={clientAppDesktopVersion}
    >
      <SidePanel
        onClose={onClose}
        version={config.isProduction ? undefined : config.appVersion}
        historyEntries={sessions}
        onSelectHistoryEntry={(entry) => select(entry.id)}
        onRenameHistoryEntry={handleRenameHistoryEntry}
        onDeleteHistoryEntry={handleDeleteHistoryEntry}
        historyLoading={sessionsLoading}
        historyError={sessionsError}
        onNewChat={startNewChat}
        welcomeCards={welcomeCards}
      />
    </AgentProvider>
  );
};
